/**
 * ProductPhotoSpec - combined 2-column product intro section placed
 * directly below the hero on each Product page (Access / Rig / Crane).
 *
 * Left column: branded product photo (previously a full-width silhouette
 * band). Right column: existing Specification block (eyebrow + title +
 * description + <SpecTable>) that used to sit lower on the page.
 *
 * Mobile: single column, photo on top, spec table below.
 *
 * Props
 *   photo        - { src, alt, caption } from PRODUCT_MEDIA
 *   specEyebrow  - eyebrow label (default "Specification")
 *   specTitle    - h3 title above the description
 *   specBody     - descriptive paragraph
 *   specRows     - rows for <SpecTable>
 *   specTableTitle - title for <SpecTable>
 *   captionOffsetX - optional horizontal nudge (px) for the figcaption.
 *                    Compensates for optical mis-centering caused by
 *                    trailing letter-spacing on uppercase captions of
 *                    a specific length. Negative = shift left. Only pass
 *                    when a specific product page's caption looks
 *                    optically off; default 0 keeps captions unshifted.
 *   imageObjectPosition - optional CSS object-position value for the
 *                    <img>. Only pass when the source photo's aspect
 *                    ratio letterboxes awkwardly within the 7:5 frame
 *                    and you want to bias the visible mat toward
 *                    "top" / "bottom" / "center". When omitted, the
 *                    default Tailwind class (object-bottom) applies.
 */

import SpecTable from './SpecTable';

const ProductPhotoSpec = ({
    photo,
    specEyebrow = 'Specification',
    specTitle,
    specBody,
    specRows,
    specTableTitle,
    captionOffsetX = 0,
    imageObjectPosition,
    testId = 'product-photo-spec',
}) => {
    const specHeadingId = `${testId}-heading`;
    return (
        <section
            className="border-y border-brand-hairline bg-brand-surface"
            data-eyebrow-tone="blue"
            data-testid={testId}
            aria-labelledby={specHeadingId}
        >
            <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 md:grid-cols-[minmax(320px,2fr)_3fr] md:items-center md:gap-16 md:px-10 md:py-24">
                {/* Left column: product photo - fixed-frame so all 3 mat
                    photos (Access/Rig/Crane) share the same visual footprint
                    regardless of source aspect. object-contain letterboxes
                    into the frame; the surrounding brand-surface fills any
                    gap so there are no visible frame edges. `md:items-center`
                    on the grid centers this compact figure box against the
                    right column's full stack (eyebrow + title + body + table)
                    as a single visual unit. */}
                <figure className="mx-auto flex w-full flex-col items-center">
                    <div className="mx-auto w-full max-w-[280px] aspect-[7/5] md:max-w-[600px]">
                        <img
                            src={photo.src}
                            alt={photo.alt}
                            loading="lazy"
                            decoding="async"
                            className="block h-full w-full object-contain object-bottom"
                            style={imageObjectPosition ? { objectPosition: imageObjectPosition } : undefined}
                            data-testid={`${testId}-image`}
                        />
                    </div>
                    {photo.caption ? (
                        <figcaption
                            className="mt-4 text-center font-display text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold sm:text-xs"
                            style={captionOffsetX ? { transform: `translateX(${captionOffsetX}px)` } : undefined}
                            data-testid={`${testId}-caption`}
                        >
                            {photo.caption}
                        </figcaption>
                    ) : null}
                </figure>

                {/* Right column: spec eyebrow + title + description + table
                    stacked in one column. Its full box height defines the row
                    height; `md:items-center` on the grid centers the compact
                    figure box against this column's midpoint. */}
                <div>
                    <span className="eyebrow">{specEyebrow}</span>
                    <h3
                        id={specHeadingId}
                        className="h-display mt-6 text-2xl sm:text-3xl"
                    >
                        {specTitle}
                    </h3>
                    {specBody ? (
                        <p className="mt-5 font-sans text-base text-brand-muted">
                            {specBody}
                        </p>
                    ) : null}
                    <div className="mt-8">
                        <SpecTable rows={specRows} title={specTableTitle} />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProductPhotoSpec;
